/**
 * Renders the main button for the Student Helper feature, visually representing
 * its current state by displaying the correct icon and style.
 */

import { CheckSquare, Square } from "lucide-react"

import { Button, ButtonProps } from "@/components/ui/Button"
import { Spinner } from "@/components/ui/Spinner"
import { cn } from "@/lib/shadcn-utils"

import { StudentHelperPreparationStatus } from "../../../../models/StudentHelperAssignmentPreparationStatus"

import { StudentHelperBetaTag } from "./StudentHelperBetaTag"

interface StudentHelperButtonProps extends ButtonProps {
  status: StudentHelperPreparationStatus
  isError: boolean
  allowInstantHelper: boolean
  isPending?: boolean
}

export function StudentHelperButton({
  status,
  isError,
  allowInstantHelper,
  isPending,
  ...props
}: StudentHelperButtonProps) {
  function getButtonVariant(): ButtonProps["variant"] {
    if (isError) {
      return "outline_grey"
    }

    switch (status) {
      case "completed":
        return allowInstantHelper ? "secondary" : "outline_grey"
      case "processing":
        return "secondary"
      case "inactive":
      default:
        return "outline_grey"
    }
  }

  function getButtonClassName() {
    if (isError) {
      return ""
    }
    switch (status) {
      case "processing":
      case "completed":
        return allowInstantHelper ? "--allowed" : "--disallowed"
      default:
        return ""
    }
  }

  function renderContent() {
    if (isPending) {
      return <Spinner />
    }

    if (isError) {
      return <Square className="tw-size-4" />
    }

    switch (status) {
      case "processing":
        return <Spinner />
      case "completed":
        return allowInstantHelper ? (
          <CheckSquare className="tw-size-4" />
        ) : (
          <Square className="tw-size-4" />
        )
      case "inactive":
      default:
        return <Square className="tw-size-4" />
    }
  }

  return (
    <Button
      variant={getButtonVariant()}
      className={cn("tw-relative", getButtonClassName())}
      {...props}
    >
      {renderContent()}
      <span>Student Helper</span>

      <StudentHelperBetaTag className="tw-absolute tw-top-0 tw-right-0 tw-translate-x-[13%] tw--translate-y-[45%] !tw-w-[30px] tw-z-10 tw-pointer-events-none" />
    </Button>
  )
}
